import { format } from "date-fns";
import { enUS, fr } from "date-fns/locale";
import PropTypes from "prop-types";
import { createContext, useReducer } from 'react';
import { parseResumeDate } from '../../utils/formatDate';
import TRANSLATIONS from './translations-data';

const AVAILABLE_LANGUAGES = [ 'fr', 'en' ];

export const TranslationContext = createContext(
    {
        displayLanguage: 'fr',
        translations: transformNested( TRANSLATIONS, 'fr' ),
        changeLanguage: () => {
        },
        formatDate: ( date, isCssf ) => {
        }
    } );

function translationReducer( state, action ) {
    if ( action.type === 'CHANGE_LANGUAGE' ) {
        const newLanguage = action.payload.displayLanguage;
        if ( !AVAILABLE_LANGUAGES.includes( newLanguage ) ) {
            throw new Error(
                `Unknown language '${ newLanguage }.` +
                ` Accepted languages are '${ AVAILABLE_LANGUAGES.join( '\', \'' ) }'.'`
            );
        }
        return {
            ...state,
            displayLanguage: action.payload.displayLanguage,
            translations: transformNested( TRANSLATIONS, action.payload.displayLanguage ),
        }
    }
}

function transformNested( obj, lang ) {
    if ( typeof obj !== 'object' || obj === null ) {
        return obj;
    }

    // Check if this is the last level (contains language keys)
    const keys = Object.keys( obj );
    if ( keys.includes( 'fr' ) || keys.includes( 'en' ) ) {
        return obj[ lang ];
    }

    // Otherwise, recurse into each property
    const result = {};
    for ( const key in obj ) {
        result[ key ] = transformNested( obj[ key ], lang );
    }
    return result;
}


export default function TranslationContextProvider( { children } ) {
    const [ translationState, dispatch ] = useReducer(
        translationReducer,
        {
            displayLanguage: 'fr',
            translations: transformNested( TRANSLATIONS, 'fr' ),
        }
    )

    function handleLanguageChange( language ) {
        dispatch( {
            type: 'CHANGE_LANGUAGE',
            payload: {
                displayLanguage: language
            }
        } )
    }

    function formatDate( dateString, isCssf = false ) {
        const date = parseResumeDate( dateString );
        if ( !date ) {
            return '';
        }

        let locale;

        if ( translationState.displayLanguage === 'en' ) {
            locale = enUS;
        } else {
            locale = fr;
        }

        if ( isCssf ) {
            return format( date, "MMM-yyyy", { locale: locale } ).replace( ".", "" ).toLowerCase();
        }

        return format( date, "MM/yyyy", { locale: locale } );
    }

    const contextValue = {
        displayLanguage: translationState.displayLanguage,
        translations: translationState.translations,
        changeLanguage: handleLanguageChange,
        formatDate: formatDate,
    }

    return <TranslationContext.Provider value={ contextValue }>
        { children }
    </TranslationContext.Provider>;
}

TranslationContextProvider.propTypes = {
    children: PropTypes.node.isRequired,
}
